import { Col, Typography } from 'antd';
import classNames from 'classnames';
import { get } from 'lodash-es';
import { FormattedMessage, useIntl } from 'react-intl';

import { formatString } from '../../../common/utils/formatUtils';
import { LabelRow } from '../../../components/typography';
import {
  ShipmentSummaryCard,
  ShipmentSummaryCardSection,
  ShipmentSummaryDateTimeFormatWithPipe,
  TwoLineLabelWithValue,
} from './shipmentSummariesCommon';
import { TwoLinesSeparator } from '../../../components/layout/layoutElements';
import { useServiceUpdateCardStyles } from './ServiceUpdateCard.styles';

function ServiceChangeWarning({
  titleId,
  children,
  className,
  sectionClassName,
  phone,
}) {
  const hasPhone = Boolean(phone);
  const { styles } = useServiceUpdateCardStyles();
  return (
    <ShipmentSummaryCard
      className={classNames(
        styles.serviceUpdateCard,
        { 'no-phone': !hasPhone },
        className
      )}
      title={
        <>
          <div>
            <FormattedMessage id={titleId} />
          </div>
          {phone && (
            <div className={styles.subtitle}>
              <FormattedMessage id="shipmentDetail.quickCustomerService" />{' '}
              <a href={`tel:${phone}`}>{phone}</a>
            </div>
          )}
        </>
      }
    >
      <ShipmentSummaryCardSection className={sectionClassName}>
        {children}
      </ShipmentSummaryCardSection>
    </ShipmentSummaryCard>
  );
}

export function ServiceUpdateCard({ shipment }) {
  const phone = get(shipment, 'serviceUpdate.customerServicePhone');
  const { styles } = useServiceUpdateCardStyles();
  return (
    <ServiceChangeWarning phone={phone} titleId="shipmentDetail.serviceUpdate">
      <>
        <div className={styles.groupTitle}>
          <FormattedMessage id="shipmentDetail.serviceUpdate.estimatedArrival" />
        </div>
        <LabelRow>
          <Col>
            <TwoLineLabelWithValue
              labelId="shipmentDetail.serviceUpdate.new"
              text={
                <ShipmentSummaryDateTimeFormatWithPipe
                  value={get(shipment, 'serviceUpdate.newEstimatedArrival')}
                />
              }
            />
          </Col>
          <Col>
            <TwoLineLabelWithValue
              labelId="shipmentDetail.serviceUpdate.original"
              text={
                <ShipmentSummaryDateTimeFormatWithPipe
                  value={get(shipment, 'serviceUpdate.oldEstimatedArrival')}
                />
              }
            />
          </Col>
        </LabelRow>
        <LabelRow>
          <Col className={styles.reason}>
            <FormattedMessage id="shipmentDetail.serviceUpdate.reason" />:{' '}
            {formatString(get(shipment, 'serviceUpdate.reason'))}
          </Col>
        </LabelRow>
      </>
    </ServiceChangeWarning>
  );
}

export function ServiceAdvisoryCard({ shipment }) {
  const { formatMessage } = useIntl();
  const { serviceAdvisory = [] } = shipment || {};
  const { styles } = useServiceUpdateCardStyles();

  return (
    <ServiceChangeWarning
      phone={serviceAdvisory?.[0]?.customerServicePhone}
      className={styles.serviceAdvisoryCard}
      sectionClassName={styles.serviceAdvisorySection}
      titleId="shipmentDetail.serviceAdvisory"
    >
      <>
        <div
          className={classNames(styles.groupTitle, styles.groupTitleAdvisory)}
        >
          <FormattedMessage
            id="shipmentDetail.serviceAdvisory.holidayAlert"
            tagName="p"
          />
          {serviceAdvisory.map(({ date, country, holidayName }) => {
            const holidayMessage = formatMessage(
              {
                id: 'shipmentDetail.serviceAdvisory.holidayMessage',
              },
              {
                date,
                country,
                holiday: holidayName,
              }
            );
            return (
              <Typography.Paragraph ellipsis title={holidayMessage}>
                {holidayMessage}
              </Typography.Paragraph>
            );
          })}
        </div>
        <TwoLinesSeparator />
        <p className={styles.availabilityParagraph}>
          <FormattedMessage id="shipmentDetail.serviceAdvisory.availability" />{' '}
          <FormattedMessage id="shipmentDetail.serviceAdvisory.confirmDelivery" />
        </p>
        <FormattedMessage
          id="shipmentDetail.serviceAdvisory.internationalOrder"
          tagName="p"
        />
      </>
    </ServiceChangeWarning>
  );
}
